import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  PauseCircleOutlined,
  Search,
  TaskAltOutlined,
} from "@mui/icons-material";
import {
  PathologyBackButton,
  PathologySubpageShell,
  PATHOLOGY_PAGE_TITLE_SX,
} from "../components/Pathology/pathologyPageChrome";
import { createSearchParams, useNavigate, useParams } from "react-router-dom";
import { DropperIcon, MessageIcon } from "../comps/CustomIcons";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import {
  getPathologyByIdApi,
  printPathologyApi,
  updatePathologyApi,
} from "../services/pathology.service";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const STEP_LABELS = [
  { label: "Pending" },
  { label: "Accepted & Generated" },
  { label: "Sample Collected" },
  { label: "In Progress" },
  { label: "Dr Approval" },
  { label: "Dispatched" },
];

const PathologyPatientProgress = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [activeStep, setActiveStep] = useState("");
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("UHID");

  useEffect(() => {
    setIsLoading(true);
    setIsError(false);
    getPathologyByIdApi({ id }).then(res => {
      console.log('Raw API response for pathology by id:', res);
      if (res && (res.success || res.code === 200) && res.data) {
        setDetailData(res.data);
      } else {
        setDetailData(null);
        setIsError(true);
      }
      setIsLoading(false);
    }).catch(err => {
      setIsError(true);
      setDetailData(null);
      setIsLoading(false);
    });
  }, [id]);

  const cancelReportHandler = async () => {
    setLoading(true);
    const response = await updatePathologyApi(id, { status: "Cancelled" });
    setLoading(false);

    if (response.code === 200 || response.code === 201) {
      toast.success("Pathology report cancelled successfully");
      getPathologyByIdApi({ id }).then(res => {
        setDetailData(res?.data || null);
      });
      navigate("/pathology");
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  const handlePrintPathology = async () => {
    if (!id) {
      toast.error("Missing pathology request id.");
      return;
    }

    setPrintLoading(true);

    const response = await printPathologyApi(id);
    setPrintLoading(false);

    if (
      !response ||
      response.success === false ||
      response.error ||
      response.isNetworkError
    ) {
      toast.error(response?.message || "Failed to fetch PDF");
      return;
    }

    const isBinary =
      response instanceof ArrayBuffer ||
      response instanceof Blob ||
      (typeof response === "object" &&
        response !== null &&
        typeof response.byteLength === "number");

    if (!isBinary) {
      toast.error("Invalid PDF data received");
      return;
    }

    const blob = new Blob([response], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);

    const tryDownload = () => {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `Pathology_Report_${id}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.info("Opened download — allow popups to print in a new tab.");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
    };

    const printWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");

    if (!printWindow) {
      tryDownload();
      return;
    }

    const triggerSystemPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch {
        /* embedded PDF viewer may block print(); user can use browser menu */
      }
    };

    printWindow.onload = triggerSystemPrint;
    setTimeout(triggerSystemPrint, 750);

    printWindow.addEventListener(
      "unload",
      () => {
        URL.revokeObjectURL(blobUrl);
      },
      { once: true }
    );
  };

  const submitHandler = async () => {
    let response;

    const payload =
      activeStep === "Pending"
        ? { status: "Accepted & Generated" }
        : activeStep === "Accepted & Generated"
        ? { status: "Sample Collected", sample: { status: "Received" } }
        : activeStep === "Sample Collected"
        ? { status: "In Progress" }
        : null;

    if (!payload) {
      toast.error("No action available for this step.");
      return;
    }

    setNextLoading(true);
    response = await updatePathologyApi(id, payload);
    setNextLoading(false);

    if (response.code === 200 || response.code === 201) {
      toast.success(`Pathology updated successfully`);
      getPathologyByIdApi({ id }).then(res => {
        setDetailData(res?.data || null);
      });
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (detailData) {
      setActiveStep(detailData.status);
    }
  }, [detailData]);

  // Search functionality
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    // If search term is empty, show all data
    if (!value.trim()) {
      return;
    }

    // Search within the current detail data
    if (detailData) {
      const searchLower = value.toLowerCase();
      let found = false;

      switch (searchType) {
        case "UHID":
          found = detailData.UHID?.toLowerCase().includes(searchLower);
          break;
        case "DATE":
          // Search in created date or other date fields
          const createdDate = detailData.createdAt ? new Date(detailData.createdAt).toLocaleDateString() : "";
          found = createdDate.includes(searchLower);
          break;
        default:
          // Search in multiple fields
          found = 
            detailData.UHID?.toLowerCase().includes(searchLower) ||
            `${detailData.patient?.firstName} ${detailData.patient?.lastName}`.toLowerCase().includes(searchLower) ||
            detailData.service?.itemName?.toLowerCase().includes(searchLower) ||
            detailData.doctor?.firstName?.toLowerCase().includes(searchLower) ||
            detailData.doctor?.lastName?.toLowerCase().includes(searchLower);
      }

      if (!found) {
        // You could show a toast or highlight that no matches found
        console.log('No matches found for search term:', value);
      }
    }
  };

  const handleSearchTypeChange = (event) => {
    setSearchType(event.target.value);
    setSearchTerm(""); // Clear search when type changes
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <PathologySubpageShell>
          <PathologyBackButton onClick={handleBack} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 0,
              mb: "20px",
              color: "#5D6A82",
            }}
          >
            <Typography sx={PATHOLOGY_PAGE_TITLE_SX}>
              Pathology Request Progress
            </Typography>
            {["Pending", "Accepted & Generated"].includes(activeStep) && (
              <TextField
                placeholder="Search"
                size="small"
                value={searchTerm}
                onChange={handleSearch}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        {activeStep === "Pending" ? (
                          <select
                            className="uid-select"
                            value={searchType}
                            onChange={handleSearchTypeChange}
                          >
                            <option value="UHID">UHID</option>
                            <option value="NAME">Name</option>
                            <option value="TEST">Test</option>
                          </select>
                        ) : (
                          <select
                            className="uid-select"
                            value={searchType}
                            onChange={handleSearchTypeChange}
                          >
                            <option value="DATE">Date</option>
                            <option value="UHID">UHID</option>
                            <option value="NAME">Name</option>
                          </select>
                        )}
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ mx: 1 }}
                        />
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          </Box>

          {/* Stepper */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              mb: "20px",
              overflow: "hidden",
            }}
          >
            {STEP_LABELS?.map((label, index) => (
              <Box
                key={label}
                sx={{
                  backgroundColor: "#5D6A82",
                  width: "18.33%",
                  height: "40px",
                  cursor: "pointer",
                  clipPath:
                    index === 0
                      ? "polygon(0% 0%,90% 0%,100% 50%, 90% 100%, 0% 100%)"
                      : "polygon(0% 0%,90% 0%,100% 50%, 90% 100%, 0% 100%, 10% 50%)",
                  display: "grid",
                  placeItems: "center",
                  mr: "-2%",
                }}
              >
                <Box
                  key={label}
                  sx={{
                    transform: "scale(0.98)",
                    backgroundColor:
                      activeStep === label.label ? "#AED5F3" : "#ECF8FB",
                    height: "38px",
                    width: "100%",
                    clipPath:
                      index === 0
                        ? "polygon(0% 0%,90% 0%,100% 50%, 90% 100%, 0% 100%)"
                        : "polygon(0% 0%,90% 0%,100% 50%, 90% 100%, 0% 100%, 10% 50%)",
                    display: "grid",
                    placeItems: "center",
                    boxSizing: "border-box",
                    pl: { lg: "30px", xl: 0 },
                    pr: { lg: "20px", xl: 0 },
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      color: "#163F62",
                      fontWeight:
                        activeStep === label.label ? "bold" : "normal",
                      fontSize: "80%",
                    }}
                  >
                    {label.label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              mb: "20px",
            }}
          >
            <Typography>Actions:</Typography>
            <Tooltip title="" arrow>
              <IconButton
                sx={{
                  bgcolor: "#F0F7FC",
                  borderRadius: "8px",
                  height: "45px",
                  width: "45px",
                }}
              >
                <TaskAltOutlined sx={{ fontSize: "22px", color: "#5D6A82" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="" arrow>
              <IconButton
                sx={{
                  bgcolor: "#F0F7FC",
                  borderRadius: "8px",
                  height: "45px",
                  width: "45px",
                }}
              >
                <DropperIcon sx={{ color: "transparent", fontSize: "25px" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="" arrow>
              <IconButton
                sx={{
                  bgcolor: "#F0F7FC",
                  borderRadius: "8px",
                  height: "45px",
                  width: "45px",
                }}
              >
                <MessageIcon sx={{ fontSize: "20px", color: "#5D6A82" }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="" arrow>
              <IconButton
                sx={{
                  bgcolor: "#F0F7FC",
                  borderRadius: "8px",
                  height: "45px",
                  width: "45px",
                }}
              >
                <PauseCircleOutlined sx={{ color: "#5D6A82" }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Step Content */}
          {/* <Box>{STEP_CONTENTS[activeStep]}</Box> */}
          <Box>
            <Box>
              {/* Patient Information */}
              <Box
                sx={{
                  border: `1px solid #CCD4DA`,
                  borderRadius: "12px",
                  p: "20px",
                  mb: "30px",
                }}
              >
                <Typography sx={{ color: "#5D6A82", mb: 2, fontSize: "14px" }}>
                  Patient Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="UHID"
                      value={detailData?.UHID || "-"}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Patient Name"
                      value={`${detailData?.patient?.firstName || ''} ${detailData?.patient?.lastName || ''}`}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Age"
                      fullWidth
                      value={(() => {
                        const dob = detailData?.patient?.dob;
                        if (!dob) return "-";
                        const birthDate = new Date(dob);
                        const today = new Date();
                        return Math.floor((today - birthDate) / (1000 * 60 * 60 * 24 * 365.25));
                      })()}
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Gender"
                      value={detailData?.patient?.gender || "-"}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Consultation"
                      value={detailData?.consultation?.visitNo || "-"}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Doctor's Name"
                      value={`${detailData?.doctor?.firstName} ${detailData?.doctor?.lastName}`}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Ward Name"
                      value={detailData?.ward?.wardName || "-"}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Bed Number"
                      value={detailData?.bedNumber?.bedNumber || "-"}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Report Details  */}
              <Box
                sx={{
                  border: `1px solid #CCD4DA`,
                  borderRadius: "12px",
                  p: "20px",
                  mb: "30px",
                }}
              >
                <Typography sx={{ color: "#5D6A82", mb: 2, fontSize: "14px" }}>
                  Report Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={activeStep === "Pending" ? 4 : 3}
                  >
                    <TextField
                      label="Item Name"
                      value={detailData?.service?.itemName || "-"}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={activeStep === "Pending" ? 4 : 3}
                  >
                    <TextField
                      label="Quantity"
                      value={detailData?.quantity || "-"}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  {activeStep !== "Pending" && (
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Amount"
                        value={detailData?.amount || "-"}
                        fullWidth
                        required
                        slotProps={{ input: { readOnly: true } }}
                      />
                    </Grid>
                  )}
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={activeStep === "Pending" ? 4 : 3}
                  >
                    <TextField
                      label="Status"
                      value={detailData?.status || "-"}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Sample Information */}
            {activeStep !== "Pending" &&
              activeStep !== "Accepted & Generated" && (
                <Box
                  sx={{
                    border: `1px solid #CCD4DA`,
                    borderRadius: "12px",
                    p: "20px",
                    mb: "30px",
                  }}
                >
                  <Typography
                    sx={{ color: "#5D6A82", mb: 2, fontSize: "14px" }}
                  >
                    Sample Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <TextField
                        label="Sample ID"
                        fullWidth
                        value={detailData?.sample?.sampleId || "-"}
                        required
                        slotProps={{ input: { readOnly: true } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <TextField
                        label="Sample Date"
                        fullWidth
                        value={
                          detailData?.sample?.sampleDate
                            ? dayjs(detailData?.sample?.sampleDate).format(
                                "DD-MM-YYYY"
                              )
                            : ""
                        }
                        required
                        slotProps={{ input: { readOnly: true } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <TextField
                        label="Sample Type"
                        value={detailData?.sample?.sampleType || "-"}
                        fullWidth
                        required
                        slotProps={{ input: { readOnly: true } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <TextField
                        label="Status"
                        fullWidth
                        value={detailData?.sample?.status || "-"}
                        required
                        slotProps={{ input: { readOnly: true } }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
            }}
          >
            <Button
              variant="outlined"
              sx={{ width: "150px", textTransform: "none" }}
              disabled={loading}
              onClick={cancelReportHandler}
            >
              {loading ? <CircularProgress size={20} /> : `Cancel Report`}
            </Button>
            {activeStep !== "Pending" && (
              <Button
                variant="outlined"
                sx={{ width: "200px", textTransform: "none" }}
              >
                Mark As Current Status
              </Button>
            )}
            <Button
              variant="contained"
              disableElevation
              sx={{
                width: "150px",
                textTransform: "none",
              }}
              disabled={printLoading}
              onClick={() => {
                if (
                  activeStep === "Dr Approval" ||
                  activeStep === "In Progress"
                ) {
                  const svc = detailData?.service;
                  const serviceId =
                    typeof svc === "object" && svc !== null ? svc._id : svc;
                  if (!serviceId || !id) {
                    toast.error(
                      "Missing test or request id. Cannot open add report."
                    );
                    return;
                  }
                  navigate(
                    {
                      pathname: `/pathology/add-report/${serviceId}`,
                      search: createSearchParams({
                        requestId: id,
                      }).toString(),
                    },
                    {
                      state: {
                        status: activeStep,
                        ID: id,
                        allData: detailData,
                      },
                    }
                  );
                } else if (activeStep === "Dispatched") {
                  handlePrintPathology();
                } else {
                  submitHandler();
                }
              }}
            >
              {activeStep === "Pending" ? (
                "Accept"
              ) : activeStep === "Accepted & Generated" ? (
                "Add Sample"
              ) : activeStep === "Sample Collected" ? (
                "Proceed"
              ) : activeStep === "In Progress" ? (
                "Add Report"
              ) : activeStep === "Dr Approval" ? (
                "Dr Approval"
              ) : activeStep === "Dispatched" ? (
                printLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  "Print"
                )
              ) : null}
            </Button>
          </Box>
        </PathologySubpageShell>
      </Box>
    </Container>
  );
};

export default PathologyPatientProgress;
